/*!
 * TinyPoker 遊戲種類數據組件
 * 顯示 Ring Games 按遊戲類型和級別的統計資訊
 * 風格參照 TinyEcoBot.js
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Input,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  UncontrolledTooltip,
} from "reactstrap";
import axios from "axios";
import classnames from "classnames";
import { Line, Bar } from "react-chartjs-2";
import { GAME_TYPES } from "constant/gameTypes";
import TinyLoading, { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
import TinyWeekRangePicker from "../../components/tiny/TinyWeekRangePicker";
import GrowthBadge from "../../components/tiny/GrowthBadge";
import { windowNav, previousWindow } from "../../components/tiny/weekWindowUtils";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyPageTabs from "components/tiny/TinyPageTabs";
import TinyStatStrip from "components/tiny/TinyStatStrip";
// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_BASE = "/api/tiny";

const numberWithCommas = (num) => {
  if (num === null || num === undefined) return "0";
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// 四種遊戲類別的單一來源（見 constant/gameTypes.js 的說明）
const gameTypeConfig = GAME_TYPES;

const DATA_TYPE_OPTIONS = [
  { key: 'real', label: '真人', icon: 'fa-user', color: '#1f8ef1' },
  { key: 'ecobot', label: 'EcoBot', icon: 'fa-robot', color: '#00d6b4' },
  // v2.28.0 自營Bot：我方自己研發的開桌機器人，跟 EcoBot（外部夥伴）是兩回事。
  // 後端 game_stats_service 的 summary / by_level / by_week / by_table_tag 都有這一桶。
  // ⚠️ 原本是 `fa-android` —— 它在 FA6 屬於 **brands** 家族，要 `fab` 前綴，
  //    而這裡渲染成 `fa ${opt.icon}`（Free 家族）→ 一直是空白的。
  { key: 'self_bot', label: '自營Bot', icon: 'fa-microchip', color: '#e14eca' },
  { key: 'total', label: '聯盟總和', icon: 'fa-globe', color: '#f5a623' },
];

// 特殊桌型篩選（僅 NLH 有意義；key 對應後端 game_stats_service.SPECIAL_TABLE_TAGS）
const TABLE_TAG_OPTIONS = [
  { key: '', label: '全部桌型' },
  { key: 'squid', label: '🦑 魷魚' },
  { key: 'bomb', label: '💣 炸彈(含超級)' },
  { key: 'super_bomb', label: '💥 超級炸彈' },
];

// EcoBot 暫時報表上傳 job 進行中的狀態（snapshot 尚未產出）
const PREVIEW_ACTIVE_JOB_STATES = ['uploading', 'importing', 'scanning', 'cleaning'];
const PREVIEW_JOB_STATUS_LABEL = {
  uploading: '上傳中', importing: '匯入中', scanning: '分析中', cleaning: '整理中',
};

// 從 by_table_tag 取某 dataType 的某指標；'bomb_all' = 一般炸彈 + 超級炸彈
const tagMetric = (byTag, tagKey, dataType, field) => {
  if (!byTag) return 0;
  if (tagKey === 'bomb_all') {
    return ((byTag.bomb?.[dataType]?.[field]) || 0) + ((byTag.super_bomb?.[dataType]?.[field]) || 0);
  }
  return (byTag[tagKey]?.[dataType]?.[field]) || 0;
};

const DataTypeSwitch = ({ value, onChange }) => {
  return (
    <div style={{
      display: 'inline-flex',
      background: '#111822',
      borderRadius: '10px',
      padding: '4px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    }}>
      {DATA_TYPE_OPTIONS.map(opt => {
        const isActive = value === opt.key;
        return (
          <button
            key={opt.key}
            onClick={() => onChange(opt.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 20px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: isActive ? '600' : '400',
              color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.5)',
              background: isActive
                ? `linear-gradient(135deg, ${opt.color}, ${opt.color}99)`
                : 'transparent',
              boxShadow: isActive ? `0 3px 12px ${opt.color}40` : 'none',
              transition: 'all 0.25s ease',
              outline: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <i className={`fa ${opt.icon}`} style={{ fontSize: '13px' }} />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

// 統一使用 ECOBOT 風格的 Nav 組件
const EnhancedNav = ({ children, ...props }) => {
  return (
    <Nav 
      {...props} 
      className="custom-nav mb-1 position-relative"
      style={{
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexWrap: 'wrap',
        background: '#111822',
        padding: '4px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        cursor: "pointer"
      }}
    >
      {children}
    </Nav>
  );
};

const EnhancedNavItem = ({ children, ...props }) => {
  return (
    <NavItem 
      {...props} 
      style={{
        margin: '2px',
        position: 'relative',
        zIndex: 1
      }}
    >
      {children}
    </NavItem>
  );
};

// ⚠️ 原本這裡有一份 EnhancedNavLink，與 TinyClubConfig / TinyClubRake /
// TinyTopPlayers **共四份一模一樣的複製**。已改用 TinyPageTabs，這份移除。

function TinyGameStats() {
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState("NLH");
  const [loading, setLoading] = useState(true);
  const [loadingText, setLoadingText] = useState('');
  const [error, setError] = useState(null);
  
  // Data states
  const [weeks, setWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [displayedWeeks, setDisplayedWeeks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [prevCategoryData, setPrevCategoryData] = useState(null);  // 上一期同 category（供 Rake 期比較）
  
  // 資料類型 switch（真人/ecobot/聯盟總和）
  const [dataType, setDataType] = useState('real');
  
  // 子類型篩選
  // `subTypeFilter` = 這個類別**可以選**的子類型清單（例如 AOF 的 AFH / AFO）。
  const [subTypeFilter, setSubTypeFilter] = useState([]);
  const [selectedSubTypes, setSelectedSubTypes] = useState([]);

  // 統計摘要底下的子 tab：級別明細 / 級別分布 / 週度趨勢
  const [detailTab, setDetailTab] = useState('table');

  // 桌型篩選（''=全部 / squid / bomb / super_bomb）
  const [tableTag, setTableTag] = useState('');

  // 暫時報表預覽（沿用 EcoBot 上傳產出的 game_stats_preview snapshot）
  const [previewData, setPreviewData] = useState(null);   // { active, preview_week, categories }
  const [previewMode, setPreviewMode] = useState(false);
  // 偵測到有進行中的上傳 job 時的狀態（用於顯示「處理中、產出後自動顯示」並輪詢）
  const [previewJobStatus, setPreviewJobStatus] = useState(null); // { status, message }
  const pollRef = useRef(null);
  
  // 級別展開/收起狀態
  const [expandedLevels, setExpandedLevels] = useState(new Set());
  
  // Week selector state

  // Result cache: avoids re-fetching when switching tabs with same weeks
  const categoryCache = useRef({});

  // 載入初始數據
  useEffect(() => {
    loadInitialData();
    return () => stopPreviewPolling();   // 卸載時停止輪詢
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPreviewPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  // 抓最新 active 預覽 snapshot，有就帶入並切到預覽模式
  const applyActivePreview = async () => {
    try {
      const res = await axios.get(`${API_BASE}/game-stats/preview`);
      if (res.data.code === 1 && res.data.data?.active) {
        setPreviewData(res.data.data);
        setPreviewMode(true);
        return true;
      }
    } catch (e) { /* 忽略 */ }
    return false;
  };

  // 偵測到有進行中的上傳 job → 輪詢；完成後自動載入預覽（比照 EcoBot 頁，免手動重整）
  const startPreviewPolling = () => {
    stopPreviewPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE}/exploit/preview-status`);
        const job = res.data?.data;
        if (job && PREVIEW_ACTIVE_JOB_STATES.includes(job.status)) {
          setPreviewJobStatus({ status: job.status, message: job.message });
          return;
        }
        // 結束（completed / failed / 無 job）
        stopPreviewPolling();
        setPreviewJobStatus(null);
        if (job && job.status === 'completed') {
          await applyActivePreview();   // 自動帶入並切到預覽
        }
      } catch (e) { /* 暫時失敗，下次再試 */ }
    }, 3000);
  };

  const loadInitialData = async () => {
    setLoading(true);
    setLoadingText('載入週期資料');
    setError(null);
    try {
      const [weeksRes, previewRes] = await Promise.all([
        axios.get(`${API_BASE}/game-stats/weeks`),
        axios.get(`${API_BASE}/game-stats/preview`).catch(() => null),
      ]);

      // 有 active 暫時報表預覽 → 預設進預覽模式（比照 EcoBot 直接顯示 GM 上傳的最新內容）
      let hasActivePreview = false;
      if (previewRes?.data?.code === 1 && previewRes.data.data?.active) {
        setPreviewData(previewRes.data.data);
        setPreviewMode(true);
        hasActivePreview = true;
      } else {
        // 還沒有 active 預覽 → 看是否有進行中的上傳 job（snapshot 還在算）；有就輪詢，完成自動顯示
        try {
          const st = await axios.get(`${API_BASE}/exploit/preview-status`);
          const job = st.data?.data;
          if (job && PREVIEW_ACTIVE_JOB_STATES.includes(job.status)) {
            setPreviewJobStatus({ status: job.status, message: job.message });
            startPreviewPolling();
          }
        } catch (e) { /* 忽略 */ }
      }

      if (weeksRes.data.code === 1) {
        const weeksList = weeksRes.data.data.weeks || [];
        setWeeks(weeksList);
        if (weeksList.length > 0) {
          const recentOneWeek = weeksList.slice(0, 1);
          setSelectedWeeks(recentOneWeek);
          setDisplayedWeeks(recentOneWeek);
          // loading 保持 true，由 displayedWeeks / previewMode effect 接管
          return;
        }
      }
      // 無正式週但有預覽（罕見）：仍由 previewMode effect 載入預覽
      if (hasActivePreview) return;
      setLoading(false);
    } catch (err) {
      console.error("載入數據失敗:", err);
      setError("載入數據失敗: " + (err.response?.data?.msg || err.message));
      setLoading(false);
    }
  };

  // 當週期改變時載入數據（清除快取）
  useEffect(() => {
    if (displayedWeeks.length > 0) {
      categoryCache.current = {};
      setLoading(true);
      setLoadingText('載入遊戲數據');
      loadSummary();
      loadCategoryData(activeTab);
    }
  }, [displayedWeeks]);

  // 當切換 tab / 子類型 / 桌型時載入該類別數據（預覽模式下也要，桌型切換要重取 snapshot 變體）
  useEffect(() => {
    if (displayedWeeks.length > 0 || (previewMode && previewData)) {
      loadCategoryData(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSubTypes, tableTag]);

  // 切換「暫時預覽 / 正式週」模式時重載（含初次偵測到 active preview）
  useEffect(() => {
    if (previewMode && previewData) {
      loadSummary();
      loadCategoryData(activeTab);
    } else if (!previewMode && displayedWeeks.length > 0) {
      categoryCache.current = {};
      loadSummary();
      loadCategoryData(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode]);

  // 切換分類時清掉第二層的篩選 —— 桌型與子類型都是 **per-category** 的。
  // 🔴 `selectedSubTypes` 先前沒有跟著清：在 AOF 選了 AFH 再切到 PLO，
  //    會帶著 `sub_type=AFH` 去查 PLO（查無資料），而且畫面上看不出原因。
  useEffect(() => {
    if (activeTab !== 'NLH' && tableTag) setTableTag('');
    setSelectedSubTypes([]);
    setSubTypeFilter([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadSummary = async () => {
    // 預覽模式：摘要直接從 snapshot 取（各 category 的 unfiltered 變體），不打 API
    if (previewMode && previewData) {
      const byCategory = {};
      const tagsByCategory = {};
      for (const cat of Object.keys(gameTypeConfig)) {
        const data = previewData.categories?.[cat]?.[''] || null;
        if (data?.summary) byCategory[cat] = data.summary;
        if (data?.by_table_tag) tagsByCategory[cat] = data.by_table_tag;
      }
      setSummary({ by_category: byCategory, tags_by_category: tagsByCategory });
      return;
    }
    try {
      const categories = Object.keys(gameTypeConfig);
      const weeksParam = displayedWeeks.join(",");

      const results = await Promise.all(
        categories.map(cat => {
          const cacheKey = `${cat}|${weeksParam}|`;
          if (categoryCache.current[cacheKey]) {
            return Promise.resolve({ category: cat, data: categoryCache.current[cacheKey] });
          }
          const params = new URLSearchParams();
          params.append("weeks", weeksParam);
          params.append("enhanced", "1");
          return axios.get(`${API_BASE}/game-stats/category/${cat}?${params}`)
            .then(res => {
              if (res.data.code === 1) {
                categoryCache.current[cacheKey] = res.data.data;
                return { category: cat, data: res.data.data };
              }
              return { category: cat, data: null };
            });
        })
      );

      const byCategory = {};
      const tagsByCategory = {};
      for (const { category: cat, data } of results) {
        if (data?.summary) {
          byCategory[cat] = data.summary;
        }
        if (data?.by_table_tag) {
          tagsByCategory[cat] = data.by_table_tag;
        }
      }
      setSummary({ by_category: byCategory, tags_by_category: tagsByCategory });
    } catch (err) {
      console.error("載入摘要失敗:", err);
    }
  };

  const loadCategoryData = async (category) => {
    // 預覽模式：從 snapshot 取對應變體（NLH 依桌型；其餘取 unfiltered），不打 API。
    // 預覽模式停用 sub_type 篩選，故不套用 selectedSubTypes。
    if (previewMode && previewData) {
      const tagKey = category === 'NLH' ? (tableTag || '') : '';
      const variants = previewData.categories?.[category] || {};
      const data = variants[tagKey] || variants[''] || null;
      if (data) {
        setCategoryData(data);
        rememberSubTypes(data.sub_types);
      }
      setLoading(false);
      return;
    }

    const subKey = selectedSubTypes.length > 0 ? selectedSubTypes[0] : '';
    const tagKey = category === 'NLH' ? tableTag : '';
    const cacheKey = `${category}|${displayedWeeks.join(',')}|${subKey}|${tagKey}`;

    const cached = categoryCache.current[cacheKey];
    if (cached) {
      setCategoryData(cached);
      rememberSubTypes(cached.sub_types);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadingText(`載入 ${category} 數據`);
    try {
      const params = new URLSearchParams();
      params.append("weeks", displayedWeeks.join(","));
      params.append("enhanced", "1");
      if (selectedSubTypes.length > 0) {
        params.append("sub_type", selectedSubTypes[0]);
      }
      if (tagKey) {
        params.append("table_tag", tagKey);
      }

      const res = await axios.get(`${API_BASE}/game-stats/category/${category}?${params}`);
      if (res.data.code === 1) {
        const data = res.data.data;
        categoryCache.current[cacheKey] = data;
        setCategoryData(data);
        rememberSubTypes(data.sub_types);
      }
    } catch (err) {
      console.error("載入類別數據失敗:", err);
      setError("載入類別數據失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 上一期（前 N 週）同 category 資料，供「與上一期比較」；預覽模式或歷史不足 → null
  const fetchPrevCategory = async () => {
    if (previewMode || !displayedWeeks.length) {
      setPrevCategoryData(null);
      return;
    }
    const prev = previousWindow(weeks, displayedWeeks);
    if (!prev || !prev.length) {
      setPrevCategoryData(null);
      return;
    }
    const params = new URLSearchParams();
    params.append("weeks", prev.join(","));
    params.append("enhanced", "1");
    if (selectedSubTypes.length > 0) params.append("sub_type", selectedSubTypes[0]);
    const tagKey = activeTab === 'NLH' ? tableTag : '';
    if (tagKey) params.append("table_tag", tagKey);
    try {
      const res = await axios.get(`${API_BASE}/game-stats/category/${activeTab}?${params}`);
      setPrevCategoryData(res.data.code === 1 ? res.data.data : null);
    } catch (e) {
      setPrevCategoryData(null);
    }
  };

  useEffect(() => {
    fetchPrevCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedWeeks, activeTab, selectedSubTypes, tableTag, previewMode]);

  // 週期選擇器
  const applyWeekSelection = () => {
    if (selectedWeeks.length === 0) {
      alert("請至少選擇一個週期");
      return;
    }
    setPreviewMode(false);   // 選了正式週 → 離開暫時預覽
    setDisplayedWeeks([...selectedWeeks]);
  };

  // 上/下 N 週：直接套用移動後的視窗（免再送出）
  const applyWeekWindow = (win) => {
    if (!win || !win.length) return;
    setPreviewMode(false);
    setSelectedWeeks(win);
    setDisplayedWeeks(win);
  };

  // 子類型篩選切換
  /**
   * 記住「這個類別有哪些子類型」。
   *
   * 🔴 **只在沒有套子類型篩選時才更新。**
   *    後端回的 `sub_types` 是**這次查詢結果裡有的**子類型 —— 一旦帶了
   *    `sub_type=AFH`，回來就只剩 `['AFH']`，而畫面的顯示條件是
   *    `subTypeFilter.length > 1` → 選項整排消失、再也點不回去
   *   （Brad 2026-09-10：「點下去之後畫面本來可以點，後來變不能點了」）。
   *
   * ⚠️ 這個守衛**三條載入路徑都要走**（快取命中 / API 回應 / 預覽 snapshot）。
   *    第一次修的時候只補了預覽那條，另外兩條照樣把清單洗掉，bug 沒好。
   *    所以收成這一支，不要在各自的 call site 重寫條件。
   */
  const rememberSubTypes = (list) => {
    if (selectedSubTypes.length === 0) setSubTypeFilter(list || []);
  };

  // 🔴 **單選**，不是多選。
  //    後端只吃 `params.append("sub_type", selectedSubTypes[0])` —— 只有第一個
  //    有作用。原本的 UI 是可複選的 ButtonGroup，選第二個看起來有反應、
  //    實際上查詢結果不會變，是會騙人的介面。
  //    `''` = 全部（不篩選）。
  const selectSubType = (subType) => setSelectedSubTypes(subType ? [subType] : []);

  // 級別展開/收起切換
  const toggleLevelExpand = (levelIdx) => {
    setExpandedLevels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(levelIdx)) {
        newSet.delete(levelIdx);
      } else {
        newSet.add(levelIdx);
      }
      return newSet;
    });
  };

  // 導航到級別詳情頁面
  const navigateToLevelDetail = (level) => {
    const params = new URLSearchParams({
      category: activeTab,
      sb: level.blinds.sb,
      bb: level.blinds.bb,
      weeks: selectedWeeks.join(',')
    });
    
    if (level.blinds.ante !== undefined && level.blinds.ante !== null) {
      params.append('ante', level.blinds.ante);
    }
    
    navigate(`/admin/tiny-game-level-detail?${params.toString()}`);
  };

  // 取得當前 dataType 的資料
  const getLevelData = (level) => level[dataType] || level.total || {};
  const getSummaryData = () => categoryData?.summary?.[dataType] || categoryData?.summary?.total || {};
  const getWeekData = (weekObj) => weekObj[dataType] || weekObj.total || {};

  // 上一期各級別 Rake 對照（by blinds_str）＋ 上一期 category 總 Rake，供 GrowthBadge
  const blindsKey = (level) =>
    level?.blinds_str || `${level?.blinds?.sb}-${level?.blinds?.bb}-${level?.blinds?.ante ?? ''}`;
  const prevLevelRakeMap = new Map();
  if (prevCategoryData?.by_level) {
    prevCategoryData.by_level.forEach((l) => prevLevelRakeMap.set(blindsKey(l), getLevelData(l).rake || 0));
  }
  const prevCatRake = prevCategoryData
    ? (prevCategoryData.summary?.[dataType]?.total_rake ?? prevCategoryData.summary?.total?.total_rake ?? null)
    : null;

  // 圖表配置
  const getLineChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: { 
          color: '#fff',
          font: { size: 12 },
          padding: 15,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            if (context.dataset.label === 'Rake') {
              label += '$' + numberWithCommas(context.parsed.y);
            } else {
              label += numberWithCommas(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#9A9A9A', font: { size: 10 } },
        grid: { color: 'rgba(255,255,255,0.05)', display: false }
      },
      y: {
        ticks: { 
          color: '#9A9A9A',
          font: { size: 10 },
          callback: function(value) {
            if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
            if (value >= 1000) return (value / 1000).toFixed(0) + 'K';
            return value;
          }
        },
        grid: { color: 'rgba(255,255,255,0.1)', lineWidth: 0.5 }
      }
    },
    interaction: { mode: 'index', intersect: false }
  });

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            label += numberWithCommas(context.parsed.y);
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#9A9A9A', font: { size: 10 } },
        grid: { display: false }
      },
      y: {
        ticks: { 
          color: '#9A9A9A',
          font: { size: 10 },
          callback: function(value) {
            if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
            if (value >= 1000) return (value / 1000).toFixed(0) + 'K';
            return value;
          }
        },
        grid: { color: 'rgba(255,255,255,0.1)', lineWidth: 0.5 }
      }
    }
  };

  // 生成級別圖表數據
  const generateLevelChartData = () => {
    if (!categoryData || !categoryData.by_level) return null;
    
    const levels = categoryData.by_level;
    const config = gameTypeConfig[activeTab];
    
    return {
      labels: levels.map(l => l.blinds_str),
      datasets: [{
        label: 'Rake',
        data: levels.map(l => getLevelData(l).rake || 0),
        backgroundColor: config.color,
      }]
    };
  };

  // 生成週度趨勢折線圖數據
  const generateWeekTrendChartData = () => {
    if (!categoryData || !categoryData.by_week) return null;
    
    const sortedWeeks = Object.values(categoryData.by_week).sort((a, b) => 
      (a.week || '').localeCompare(b.week || '')
    );

    if (sortedWeeks.length === 0) return null;
    
    const datasets = [];
    
    // 玩家數線 (橘色)
    datasets.push({
      label: '玩家數',
      data: sortedWeeks.map(w => getWeekData(w).players || 0),
      borderColor: '#f5a623',
      backgroundColor: 'rgba(245, 166, 35, 0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#f5a623',
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    });
    
    // 手數線 (綠色)
    datasets.push({
      label: '手數',
      data: sortedWeeks.map(w => getWeekData(w).hands || 0),
      borderColor: '#00d6b4',
      backgroundColor: 'rgba(0, 214, 180, 0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#00d6b4',
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    });
    
    // Rake 線 (使用類別顏色)
    const config = gameTypeConfig[activeTab];
    datasets.push({
      label: 'Rake',
      data: sortedWeeks.map(w => getWeekData(w).rake || 0),
      borderColor: config.color,
      backgroundColor: config.bgColor,
      borderWidth: 3,
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: config.color,
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    });
    
    // 水前輸贏扣除JP 線 (紫色)
    datasets.push({
      label: '水前輸贏扣除JP',
      data: sortedWeeks.map(w => getWeekData(w).pre_rake_pl_exclude_jp || 0),
      borderColor: '#ba68c8',
      backgroundColor: 'rgba(186, 104, 200, 0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#ba68c8',
      pointBorderColor: '#fff',
      pointBorderWidth: 2
    });
    
    return {
      labels: sortedWeeks.map(w => w.week),
      datasets: datasets
    };
  };

  if (loading && !categoryData) {
    return (
      <div className="content">
        <TinyLoading text="載入遊戲種類數據" />
      </div>
    );
  }

  if (error && !categoryData) {
    return (
      <div className="content">
        <Alert color="danger">{error}</Alert>
      </div>
    );
  }

  const currentSummary = getSummaryData();
  const totalRakeForPercent = currentSummary.total_rake || 0;

  return (
    <div className="content">
      {/* 切週 / 換資料來源的重抓：整頁都是同一份資料，蓋一層 fixed 遮罩、舊資料留著 */}
      <TinyLoadingOverlay fixed active={loading && categoryData !== null} text={loadingText}>

      {/* 暫時報表「處理中」提示：偵測到 EcoBot 上傳 job 還在跑，snapshot 產出後自動顯示 */}
      {previewJobStatus && (
        <Alert color="info" style={{ marginBottom: '16px' }}>
          <TinyBusy style={{ marginRight: 8 }} />
          偵測到暫時報表正在處理中（{PREVIEW_JOB_STATUS_LABEL[previewJobStatus.status] || previewJobStatus.status}）…
          產出後會<strong>自動顯示</strong>，無需手動重新整理。
        </Alert>
      )}

      {/* 暫時報表預覽警示 banner */}
      {previewMode && previewData?.active && (
        <Alert color="warning" style={{ marginBottom: '16px' }}>
          <i className="fa fa-exclamation-triangle mr-2" />
          目前顯示 GM 上傳的<strong>暫時報表（預覽）</strong>，範圍 <strong>{previewData.preview_week}</strong>。
          此為非正式資料、僅供提前偵測異常，正式週報產生後會自動回歸以週為單位。
        </Alert>
      )}

      {/* ⚠️ 這頁的名字改過兩次：「遊戲數據統計」→「遊戲統計」→「遊戲種類數據」。
          側欄（routes.js）與這裡是同一個名字，改一個就要改另一個。
          範圍控制（可多選的週期下拉 + 前後窗格導覽）進 header；
          🚫「看暫時預覽 / 切回正式週」是**切換資料來源的動作**、
             DataTypeSwitch 是**同頁並存的 filter** —— 兩者都不進 header，
             改放在 tab 下方的控制列，跟它們作用的資料在一起。 */}
      <TinyPageHeader
        title="遊戲種類數據"
        description="各遊戲類型的桌數、手數與 Rake 表現，可多選週期比較。"
        scope={
          <TinyWeekRangePicker
            options={weeks}
            selectedValues={selectedWeeks}
            onChange={setSelectedWeeks}
            onApply={applyWeekSelection}
            onCancel={() => setSelectedWeeks([...displayedWeeks])}
            /* ⚠️ 預覽模式看的是單一份上傳檔，沒有「往前後移 N 週」可言 →
                  這時不給導航（`nav={null}`），不是給了再 disable。 */
            nav={previewMode ? null : windowNav(weeks, displayedWeeks)}
            onWindowChange={applyWeekWindow}
          />
        }
        /* ⚠️ 這一頁**刻意沒有 header tab**（全站唯一的例外）。
              遊戲種類的切換由下面那排卡片負責 —— 先前兩者並存，是同一個 state
              的兩個入口、兩種視覺語言；該留的是卡片，因為它同時帶著各類別的
              Rake / 水前扣JP / 手數，tab 只有名字（Brad 2026-09-10 拍板）。 */
      />

      {/* 資料類型與預覽切換 —— 它們改變的是「這份資料怎麼算 / 從哪來」，
          屬於資料區的控制列，不屬於標題。 */}
      <div className="d-flex flex-wrap align-items-center justify-content-end mb-3" style={{ gap: 10 }}>
        {previewData?.active && (
          <Button
            size="sm"
            color={previewMode ? "secondary" : "warning"}
            outline={previewMode}
            onClick={() => setPreviewMode(m => !m)}
            title="切換暫時報表預覽 / 正式週"
          >
            {previewMode ? '📅 切回正式週' : '📋 看暫時預覽'}
          </Button>
        )}
        <DataTypeSwitch value={dataType} onChange={setDataType} />
      </div>

      {/* ⚠️ 這四張卡**就是 header 那組 tab 的複製品** —— 點下去是
             `setActiveTab(category)`，只是多帶了數字，並用 `scale(1.05)`
             表示 active。一個東西同時當「展示」又當「選擇器」，所以讀起來
             四不像（Brad 2026-09-10：「有必要這樣做嗎…至少希望能縮小一點」）。
          → 保留它真正的價值（**四個類別並排比較**，這是 tab 給不了的），
             拿掉選擇器的身分：不再放大、active 只用該類別色描邊 + 淡染
             跟上面的 tab 對應。仍然可點（點了跟切 tab 一樣），
             但它不再是唯一的切換途徑，所以不需要長得像按鈕。
          ⚠️ 高度從 272px 壓到約 92px。 */}
      {/* 🔑 這排卡片是**這一頁唯一的遊戲種類切換器**（header 沒有 tab），
             所以「可以按」必須講清楚，不能只靠 cursor：
             ① role="tab" + aria-selected（讀屏與鍵盤）
             ② 未選中：hover 會亮起來、邊框變色
             ③ 選中：該類別色的粗邊 + 淡染底 + 右上角一個勾
             ④ 底部一條該類別色的粗線 —— 像實體分頁夾，明示「下面的內容是它的」 */}
      {summary && (
        <div role="tablist" style={{
          display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', gap: 8, marginBottom: 16,
        }}>
          {Object.keys(gameTypeConfig).map(category => {
            const g = gameTypeConfig[category];
            const d = summary.by_category[category]?.[dataType] || {};
            const on = activeTab === category;
            const pl = d.total_pre_rake_pl_exclude_jp || 0;
            return (
              <button
                key={category}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setActiveTab(category)}
                title={on ? `目前顯示 ${g.name}` : `點擊改看 ${g.name}`}
                onMouseEnter={(e) => {
                  if (on) return;
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.borderColor = `${g.color}88`;
                }}
                onMouseLeave={(e) => {
                  if (on) return;
                  e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
                }}
                style={{
                  position: 'relative', flex: '1 1 175px', minWidth: 160, textAlign: 'left',
                  // 邊框恆 2px、只換顏色 → 切換時不會有尺寸跳動
                  border: `2px solid ${on ? g.color : 'rgba(255,255,255,0.12)'}`,
                  borderBottomWidth: 4,
                  borderBottomColor: on ? g.color : 'rgba(255,255,255,0.06)',
                  background: on ? g.bgColor : 'rgba(255,255,255,0.02)',
                  borderRadius: 10, padding: '11px 14px',
                  cursor: on ? 'default' : 'pointer',
                  transition: 'background 0.16s ease, border-color 0.16s ease',
                }}
              >
                {on && (
                  <span style={{
                    position: 'absolute', top: 8, right: 10,
                    color: g.color, fontSize: 12,
                  }}><i className="fa fa-circle-check" /></span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <i className={g.icon} style={{ color: g.color, fontSize: 13 }} />
                  <span style={{ color: on ? g.color : 'rgba(255,255,255,0.72)',
                                 fontSize: 13, fontWeight: 700 }}>{g.name}</span>
                </div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.3rem', lineHeight: 1.2 }}>
                  ${numberWithCommas(d.total_rake || 0)}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
                  水前扣JP{' '}
                  <span style={{ color: pl >= 0 ? '#00d6b4' : '#fd5d93', fontWeight: 600 }}>
                    ${numberWithCommas(pl)}
                  </span>
                  {' · '}{numberWithCommas(d.total_hands || 0)} 手
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* 類別詳細內容 */}
      <TabContent activeTab={activeTab}>
        {Object.keys(gameTypeConfig).map(category => (
          <TabPane tabId={category} key={category}>
            {categoryData && activeTab === category && (
              <>
                {/* ── 第二層：類別底下的子項 ──
                    NLH → 桌型（全部 / 魷魚 / 炸彈 / 超級炸彈，帶 Rake 與佔比）
                    其餘 → 子類型（AOF 的 AFH/AFO、PLO 的 PLO/PLO5/PLO6…，由後端給）
                    🔑 兩者**用同一個版型**（Brad 2026-09-10：「子項目做得跟 NLH 一樣」）——
                       它們在資訊架構上是同一件事：都是「這個類別底下要看哪一塊」。
                    🔑 第二層一律用 **chip**（膠囊、單行），跟第一層的卡片形態完全不同，
                       層級才會一眼分得開，不是靠比大小。
                    ⚠️ 子類型沒有 per-item 的金額（後端只回名稱清單），所以那排只有標籤；
                       桌型有就顯示。不要為了對齊而硬湊數字。 */}
                {(() => {
                  const g = gameTypeConfig[category];
                  let chips = null;
                  let current = '';
                  let onPick = () => {};

                  if (category === 'NLH' && summary?.tags_by_category?.NLH) {
                    const byTag = summary.tags_by_category.NLH;
                    const nlhTotal = summary?.by_category?.NLH?.[dataType]?.total_rake || 0;
                    const pct = (v) => (nlhTotal > 0 ? (v / nlhTotal * 100) : 0);
                    const anyData = ['squid', 'bomb', 'super_bomb'].some(t => (byTag[t]?.total?.rake || 0) > 0);
                    if (!anyData) return null;
                    current = tableTag;
                    onPick = setTableTag;
                    chips = [
                      { key: '', label: '全部桌型', color: '#9aa5b8', amount: nlhTotal },
                      { key: 'squid', label: '🦑 魷魚', color: '#1f8ef1', tagKey: 'squid' },
                      { key: 'bomb', label: '💣 炸彈（含超級）', color: '#fd5d93', tagKey: 'bomb_all' },
                      { key: 'super_bomb', label: '💥 超級炸彈', color: '#e14eca', tagKey: 'super_bomb' },
                    ].map(c => {
                      const rake = c.tagKey ? tagMetric(byTag, c.tagKey, dataType, 'rake') : c.amount;
                      return {
                        ...c, amount: rake,
                        pct: c.tagKey ? pct(rake) : null,
                        hint: c.tagKey
                          ? `真人 $${numberWithCommas(tagMetric(byTag, c.tagKey, 'real', 'rake'))} · Eco $${numberWithCommas(tagMetric(byTag, c.tagKey, 'ecobot', 'rake'))}`
                          : '不篩選桌型',
                      };
                    });
                  } else if (!previewMode && subTypeFilter.length > 1) {
                    // ⚠️ 顯示條件看的是 `subTypeFilter`（**未篩選時**取得的完整清單），
                    //    不是當次查詢結果 —— 見 loadCategoryData 裡的說明。
                    current = selectedSubTypes[0] || '';
                    onPick = selectSubType;
                    chips = [{ key: '', label: `全部 ${g.name}`, color: '#9aa5b8' }]
                      .concat(subTypeFilter.map(t => ({ key: t, label: t, color: g.color })));
                  }

                  if (!chips) return null;
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                      gap: 6, marginBottom: 16, marginLeft: 20,
                      paddingLeft: 14, borderLeft: `2px solid ${g.color}55`,
                    }}>
                      <span style={{
                        color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 600,
                        whiteSpace: 'nowrap', paddingRight: 4, flexShrink: 0,
                      }}>
                        <i className="fa fa-turn-up fa-rotate-90 mr-1" style={{ fontSize: 10 }} />
                        {g.name} {category === 'NLH' ? '桌型' : '子類型'}
                      </span>
                      {chips.map(c => {
                        const on = current === c.key;
                        return (
                          <button
                            key={c.key || 'all'}
                            type="button"
                            onClick={() => onPick(c.key)}
                            title={c.hint || (c.key ? `只看 ${c.label}` : '不篩選')}
                            onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                            onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 7,
                              height: 30, boxSizing: 'border-box', padding: '0 12px',
                              borderRadius: 999,
                              border: `1px solid ${on ? c.color : 'rgba(255,255,255,0.14)'}`,
                              background: on ? `${c.color}26` : 'transparent',
                              color: on ? '#fff' : 'rgba(255,255,255,0.6)',
                              fontSize: 12, fontWeight: on ? 600 : 500, lineHeight: 1,
                              whiteSpace: 'nowrap', cursor: on ? 'default' : 'pointer',
                              transition: 'background 0.16s ease, border-color 0.16s ease, color 0.16s ease',
                            }}
                          >
                            {c.label}
                            {c.amount !== undefined && c.amount !== null && (
                              <span style={{
                                fontFamily: 'monospace', fontWeight: 700,
                                color: on ? c.color : 'rgba(255,255,255,0.75)',
                              }}>
                                ${numberWithCommas(c.amount)}
                              </span>
                            )}
                            {c.pct !== undefined && c.pct !== null && (
                              <span style={{ color: on ? c.color : 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                                {c.pct.toFixed(1)}%
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* 摘要統計 — 移除總桌數，新增總玩家數 */}
                <Row>
                  <Col md={12}>
                    <Card>
                      {/* ⚠️ 這裡原本有一整條 CardHeader：「{類別} 統計摘要」標題
                             ＋ 右邊一組子類型的 ButtonGroup。兩者都移除了 ——
                             ① 標題是多餘的：上面已經選了 NLH、又選了桌型，
                                再用一個小標題說「你現在在看 NLH」沒有增加任何資訊
                               （Brad 2026-09-10：「整個 title 都不用…直接顯示摘要就好」）
                             ② ButtonGroup 已經併進上面那排第二層 chip。 */}
                      <CardBody style={{ paddingTop: 14, paddingBottom: 14 }}>
                        {/* ⚠️ 五個總計原本是五個置中的 `<h2>`（整塊約 160px）。
                               這一頁本來就有四層控制項，摘要再佔那麼高，主表就被
                               推到摺線以下 → 換成公版的 TinyStatStrip（約 57px）。
                            ⚠️ 「總玩家數」的 tooltip（跨級別去重的定義）保留 ——
                               那是口徑說明，拿掉的話這個數字會被誤讀成加總。 */}
                        <TinyStatStrip
                          style={{ marginBottom: 0 }}
                          items={[
                            {
                              label: (
                                <>
                                  總玩家數
                                  <i className="fa fa-circle-info ml-1" id="totalPlayerInfo"
                                     style={{ cursor: 'help', fontSize: '0.9em' }} />
                                  <UncontrolledTooltip target="totalPlayerInfo" placement="bottom">
                                    在所選時間區間內，跨所有級別的不重複玩家數量。同一玩家在多個級別、多個週期打過仍只計算一次。
                                  </UncontrolledTooltip>
                                </>
                              ),
                              icon: 'fa-users', color: '17, 205, 239',
                              iconColor: gameTypeConfig[category].color,
                              value: numberWithCommas(currentSummary.total_players || 0),
                            },
                            {
                              label: '總手數', icon: 'fa-hand', color: '102, 126, 234',
                              iconColor: gameTypeConfig[category].color,
                              value: numberWithCommas(currentSummary.total_hands || 0),
                            },
                            {
                              label: '總 Rake', icon: 'fa-dollar', color: '45, 206, 137',
                              iconColor: gameTypeConfig[category].color,
                              value: `$${numberWithCommas(currentSummary.total_rake || 0)}`,
                            },
                            {
                              label: '水前輸贏扣除JP', icon: 'fa-scale-balanced',
                              color: (currentSummary.total_pre_rake_pl_exclude_jp || 0) >= 0 ? '0, 214, 180' : '253, 93, 147',
                              iconColor: (currentSummary.total_pre_rake_pl_exclude_jp || 0) >= 0 ? '#00d6b4' : '#fd5d93',
                              value: (
                                <span style={{ color: (currentSummary.total_pre_rake_pl_exclude_jp || 0) >= 0 ? '#00d6b4' : '#fd5d93' }}>
                                  ${numberWithCommas(currentSummary.total_pre_rake_pl_exclude_jp || 0)}
                                </span>
                              ),
                            },
                            {
                              label: '週數', icon: 'fa-calendar', color: '251, 99, 64',
                              iconColor: gameTypeConfig[category].color,
                              value: currentSummary.weeks_count || 0,
                            },
                          ]}
                        />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* ⚠️ 這三塊原本是「圖(收合) → 表 → 圖(收合)」平鋪往下捲。
                       Brad 2026-09-10：改成統計摘要底下的**子 tab**，收合就不需要了。
                    🔑 命名照內容取，不用「摘要／明細」這種空泛的分法：
                       級別明細＝逐級別的表、級別分布＝同一份資料的長條圖、
                       週度趨勢＝換成時間軸（不同的東西，所以獨立一個 tab）。
                    ⚠️ 這是**第三層** tab（遊戲種類 → 桌型 → 這裡），所以用 bare
                       版型、不自成一張卡，避免跟上面兩層搶視覺重量。 */}
                <TinyPageTabs
                  bare
                  style={{ marginBottom: 14 }}
                  items={[
                    { key: 'table', label: '級別明細', icon: 'fa fa-table-list' },
                    { key: 'levels', label: '級別分布', icon: 'fa fa-chart-column' },
                    { key: 'weeks', label: '週度趨勢', icon: 'fa fa-chart-line' },
                  ]}
                  active={detailTab}
                  onChange={setDetailTab}
                />

                {detailTab === 'table' && (
                  <Card>
                  <CardHeader>
                  <CardTitle tag="h4">
                  <i className="fa fa-table mr-2"></i>
                  各級別詳細數據
                  {!previewMode && (
                  <span style={{ marginLeft: 12, fontSize: '0.6em', fontWeight: 400, color: '#9A9A9A' }}>
                  Rake 趨勢（較上一期）：
                  <GrowthBadge current={getSummaryData().total_rake || 0} previous={prevCatRake ?? undefined} size="1em" />
                  </span>
                  )}
                  </CardTitle>
                  </CardHeader>
                  <CardBody>
                  <Table responsive className="table-hover">
                  <thead className="text-primary">
                  <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>#</th>
                  <th>級別 (Blinds)</th>
                  <th>遊戲類型</th>
                  <th className="text-right">
                  玩家數
                  <i 
                  className="fa fa-info-circle ml-1" 
                  id="levelPlayerInfo"
                  style={{ cursor: 'pointer', fontSize: '0.85em', color: '#9A9A9A' }}
                  ></i>
                  <UncontrolledTooltip target="levelPlayerInfo" placement="top">
                  在所選時間區間內，該級別的不重複玩家數量。同一玩家即使在多週、多桌打過也只計算一次。
                  </UncontrolledTooltip>
                  </th>
                  <th className="text-right">手數</th>
                  <th className="text-right" style={{ backgroundColor: 'rgba(31, 142, 241, 0.15)' }}>Rake</th>
                  <th className="text-right">P&L (水後)</th>
                  <th className="text-right">水前輸贏</th>
                  <th className="text-right" style={{ backgroundColor: 'rgba(186, 104, 200, 0.15)' }}>水前輸贏扣除JP</th>
                  <th className="text-right">Rake佔比</th>
                  <th className="text-center">操作</th>
                  </tr>
                  </thead>
                  <tbody>
                  {categoryData.by_level.map((level, idx) => {
                  const isExpanded = expandedLevels.has(idx);
                  const ld = getLevelData(level);
                  const gameTypes = ld.game_types || {};
                  const hasSubTypes = Object.keys(gameTypes).length > 0;
                                
                  return (
                  <React.Fragment key={idx}>
                  <tr 
                  style={{ 
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  cursor: hasSubTypes ? 'pointer' : 'default',
                  fontWeight: '500'
                  }}
                  >
                  <td onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  {hasSubTypes && (
                  <i 
                  className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`}
                  style={{ color: gameTypeConfig[category].color }}
                  />
                  )}
                  </td>
                  <td onClick={() => hasSubTypes && toggleLevelExpand(idx)}>{idx + 1}</td>
                  <td onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  <Badge color="info" style={{ fontSize: '0.95em', padding: '6px 12px' }}>
                  {level.blinds_str}
                  </Badge>
                  </td>
                  <td onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  {level.is_hu ? (
                  <Badge style={{ marginRight: '5px', fontSize: '0.85em', backgroundColor: '#e91e63', color: '#fff' }}>
                  NLH(HU)
                  </Badge>
                  ) : (
                  Object.keys(gameTypes).map(gt => (
                  <Badge key={gt} color="secondary" style={{ marginRight: '5px', fontSize: '0.85em' }}>
                  {gt}
                  </Badge>
                  ))
                  )}
                  </td>
                  <td className="text-right" onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  {numberWithCommas(ld.players || 0)}
                  </td>
                  <td className="text-right" onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  {numberWithCommas(ld.hands || 0)}
                  </td>
                  <td className="text-right" style={{ color: gameTypeConfig[category].color, fontWeight: 'bold', fontSize: '1.05em', backgroundColor: 'rgba(31, 142, 241, 0.08)' }} onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  <div>${numberWithCommas(ld.rake || 0)}</div>
                  {!previewMode && (
                  <GrowthBadge current={Number(ld.rake || 0)} previous={prevLevelRakeMap.get(blindsKey(level))} />
                  )}
                  </td>
                  <td className="text-right" onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  ${numberWithCommas(ld.pl || 0)}
                  </td>
                  <td className="text-right" onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  ${numberWithCommas(ld.pre_rake_pl || 0)}
                  </td>
                  <td className="text-right" style={{ backgroundColor: 'rgba(186, 104, 200, 0.08)' }} onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  <span style={{ color: (ld.pre_rake_pl_exclude_jp || 0) >= 0 ? '#00d6b4' : '#fd5d93', fontWeight: '600' }}>
                  ${numberWithCommas(ld.pre_rake_pl_exclude_jp || 0)}
                  </span>
                  </td>
                  <td className="text-right" onClick={() => hasSubTypes && toggleLevelExpand(idx)}>
                  <Badge size="lg" color="danger" style={{ fontSize: '0.95em', padding: '6px 12px' }}>
                  {totalRakeForPercent > 0
                  ? ((ld.rake / totalRakeForPercent) * 100).toFixed(1)
                  : '0.0'}%
                  </Badge>
                  </td>
                  <td className="text-center">
                  {/* 預覽模式隱藏：進階數據頁即時直查 ring_game_details、無 snapshot，會空白 */}
                  {!previewMode && (
                  <Button
                  size="sm"
                  color="info"
                  onClick={(e) => {
                  e.stopPropagation();
                  navigateToLevelDetail(level);
                  }}
                  style={{
                  padding: '6px 16px',
                  background: 'linear-gradient(135deg, #1f8ef1 0%, #00d6b4 100%)',
                  border: 'none',
                  borderRadius: '20px',
                  fontWeight: '600',
                  fontSize: '13px',
                  boxShadow: '0 4px 12px rgba(31, 142, 241, 0.3)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(31, 142, 241, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(31, 142, 241, 0.3)';
                  }}
                  >
                  <i className="fa fa-bar-chart mr-1" />
                  查看進階數據
                  </Button>
                  )}
                  </td>
                  </tr>
                  {/* 子類型詳情（展開時顯示） */}
                  {isExpanded && hasSubTypes && Object.entries(gameTypes).map(([gt, gtData]) => (
                  <tr 
                  key={`${idx}-${gt}`} 
                  style={{ 
                  fontSize: '0.9em', 
                  color: '#9A9A9A',
                  backgroundColor: 'rgba(0,0,0,0.3)'
                  }}
                  >
                  <td></td>
                  <td></td>
                  <td style={{ paddingLeft: '35px' }}>
                  <i className="fa fa-angle-right mr-2" style={{ color: gameTypeConfig[category].color }}></i>
                  {gt}
                  </td>
                  <td></td>
                  <td className="text-right">-</td>
                  <td className="text-right">{numberWithCommas(gtData.hands)}</td>
                  <td className="text-right" style={{ color: '#9A9A9A' }}>
                  ${numberWithCommas(gtData.rake)}
                  </td>
                  <td className="text-right">-</td>
                  <td className="text-right">-</td>
                  <td className="text-right">-</td>
                  <td className="text-right">
                  {ld.rake > 0 ? ((gtData.rake / ld.rake) * 100).toFixed(1) : '0.0'}%
                  </td>
                  <td></td>
                  </tr>
                  ))}
                  </React.Fragment>
                  );
                  })}
                  {categoryData.by_level.length === 0 && (
                  <tr>
                  <td colSpan="12" className="text-center text-muted">
                  <i className="fa fa-inbox fa-2x mb-2"></i>
                  <p>暫無數據</p>
                  </td>
                  </tr>
                  )}
                  </tbody>
                  </Table>
                  </CardBody>
                  </Card>
                )}

                {detailTab === 'levels' && (
                  <Card>
                    <CardBody>
                      {categoryData.by_level.length > 0 ? (
                        <div style={{ height: '300px' }}>
                        {generateLevelChartData() && (
                        <Bar data={generateLevelChartData()} options={barChartOptions} />
                        )}
                        </div>
                      ) : (
                        <div className="text-center text-muted p-4">這個條件下沒有級別資料。</div>
                      )}
                    </CardBody>
                  </Card>
                )}

                {detailTab === 'weeks' && (() => {
                  const weekCount = Object.keys(categoryData.by_week).length;
                  const trend = generateWeekTrendChartData();
                  const hasSeries = trend && trend.datasets.length > 0;
                  return (
                    <Card>
                      <CardBody>
                        {/* ⚠️ 只選一週時，圖表**畫得出來但只有一個點** —— 那不是趨勢，
                               而畫面上不會有任何東西說明這件事（Brad 2026-09-10）。
                            🔴 文案**不要叫使用者去按「上 N 週 / 下 N 週」** ——
                               `shiftWindow()` 是把視窗**整段平移**、`size` 不變，
                               N=1 時按幾次都只是換成另一個單週，永遠不會變成 2 週。
                               唯一能增加週數的入口是週期下拉的**多選**。
                            🚫 但也**不要在文案裡解釋這件事** —— 使用者不需要知道
                               哪條路走不通，只需要知道要做什麼。
                            🔑 警告放在圖上面、直接講「要做什麼」。圖仍然留著
                              （那一個點是真實數字，只是不構成趨勢），
                               不要讓 tab 變成一片空白。 */}
                        {weekCount < 2 && (
                          <Alert color="warning" style={{
                            background: 'rgba(251,99,64,0.12)',
                            border: '1px solid rgba(251,99,64,0.4)',
                            color: '#fb6340', marginBottom: 16,
                          }}>
                            <i className="fa fa-triangle-exclamation mr-2" />
                            <strong>目前只選了 1 週，畫不出趨勢。</strong>
                            <span style={{ color: 'rgba(255,255,255,0.7)', marginLeft: 6 }}>
                              請點右上角的「選擇週期」，一次<b>勾選 2 週以上</b>再套用。
                            </span>
                          </Alert>
                        )}
                        {hasSeries ? (
                          <div style={{ height: '350px' }}>
                            <Line data={trend} options={getLineChartOptions()} />
                          </div>
                        ) : (
                          <div className="text-center text-muted p-4">
                            <i className="fa fa-circle-info mr-2" />
                            這個條件下沒有可畫的資料。
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  );
                })()}

              </>
            )}
          </TabPane>
        ))}
      </TabContent>
      </TinyLoadingOverlay>
    </div>
  );
}

export default TinyGameStats;
